/**
 * ChatMessageBubble
 *
 * Burbuja de mensaje compartida entre la vista de chat (/chat/:agentId) y el
 * panel flotante sobre la vista del agente (FloatingChatPanel). Antes cada
 * pantalla tenía su propia copia del markup, y ambas usaban
 * `bgcolor: 'grey.100'` + `color: 'text.primary'`:
 *
 *   - `grey.100` es un valor FIJO de MUI (#f5f5f5), no cambia con el modo.
 *   - `text.primary` en dark es casi blanco (#eaf2fb).
 *   → texto casi blanco sobre burbuja casi blanca = mensaje invisible en dark.
 *
 * Además el `color` del Paper no llegaba al Typography porque Typography
 * define su propio color desde el tema; por eso el texto de las burbujas
 * "admin" sólo se veía bien en dark por casualidad.
 *
 * Fix: burbuja mode-aware + `color="inherit"` en el Typography.
 */

import { Avatar, Box, Paper, Typography } from '@mui/material';
import { Computer as ComputerIcon, Person as PersonIcon } from '@mui/icons-material';
import type { ChatMessage } from '@/types/chat';
import { agentBubbleBg } from './bubbleStyles';

export interface ChatMessageBubbleProps {
  msg: ChatMessage;
  /** Versión compacta para el panel flotante (avatar y paddings más chicos). */
  compact?: boolean;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function ChatMessageBubble({ msg, compact = false }: ChatMessageBubbleProps) {
  const isAdmin = msg.senderType === 'admin';
  const avatarSize = compact ? 24 : 28;
  const avatarGap = compact ? 0.75 : 1;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isAdmin ? 'flex-end' : 'flex-start',
        mb: compact ? 0.75 : 1,
      }}
    >
      {!isAdmin && (
        <Avatar
          sx={{
            bgcolor: 'grey.300',
            width: avatarSize,
            height: avatarSize,
            mr: avatarGap,
            mt: 0.5,
          }}
        >
          <ComputerIcon sx={{ fontSize: compact ? 14 : 16 }} />
        </Avatar>
      )}
      <Box sx={{ maxWidth: '70%' }}>
        {!isAdmin && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {msg.senderName}
          </Typography>
        )}
        <Paper
          elevation={0}
          sx={{
            px: compact ? 1.25 : 1.5,
            py: compact ? 0.75 : 1,
            borderRadius: compact ? 1.5 : 2,
            bgcolor: isAdmin ? 'primary.main' : agentBubbleBg,
            color: isAdmin ? 'primary.contrastText' : 'text.primary',
          }}
        >
          <Typography
            variant="body2"
            color="inherit"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {msg.content}
          </Typography>
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.25,
            textAlign: isAdmin ? 'right' : 'left',
            mx: 0.5,
            ...(compact ? { fontSize: '0.65rem' } : {}),
          }}
        >
          {formatTime(msg.createdAt)}
        </Typography>
      </Box>
      {isAdmin && (
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: avatarSize,
            height: avatarSize,
            ml: avatarGap,
            mt: 0.5,
          }}
        >
          <PersonIcon sx={{ fontSize: compact ? 14 : 16 }} />
        </Avatar>
      )}
    </Box>
  );
}

export default ChatMessageBubble;
